
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  InlineResponse200CorrectionSuggestion,
  SalesQuote,
  SalesQuoteItem,
  SapProduct,
} from '@/api/model';
import BaseFieldQuantity from '@/components/form/BaseFieldQuantity.vue';
import NewOrderTooltip from '@/components/orders/new-order/NewOrderTooltip.vue';
import { OrderSuggestionCorrectionSuggestion } from '@/types/ApiInterfaces';
import BaseCurrencyAmount from '@/components/BaseCurrencyAmount.vue';
import { ClaimTotals } from '@/types/ClaimTotals';
import { SapProductForCustomer } from '@/types/SapProductForCustomer';

@Component({
  components: { BaseCurrencyAmount, NewOrderTooltip, BaseFieldQuantity },
})
export default class SelectProductGroup extends Vue {
  @Prop(String)
  groupLabel!: string;

  @Prop(Array)
  products!: SapProduct[];

  @Prop({ type: Object, default: () => {} })
  agreement!: SalesQuote;

  @Prop({ type: String, default: 'order' })
  type!: string;

  @Prop({ type: Object, default: undefined })
  containerSuggestion!: InlineResponse200CorrectionSuggestion | undefined;

  @Prop({ type: Boolean, default: false })
  filled!: boolean;

  @Prop({ type: String, default: 'order' })
  cartType!: 'order' | 'claim';

  @Prop({ type: Boolean, default: true })
  customText!: boolean;

  get active(): boolean {
    return this.$store.getters[`${this.cartType}/isActiveGroup`]({
      group: this.groupLabel,
      user: this.$store.state.auth.user.username,
    });
  }

  get sortedProducts(): SapProduct[] {
    const sorted = [...this.products];
    return sorted.sort((a: SapProduct, b: SapProduct) =>
      // eslint-disable-next-line
      ((a.ProductText || '') > (b.ProductText || '') ? 1 : -1),
    );
  }

  get fillStatus(): string {
    return this.$store.getters['container/status'];
  }

  get containerSuggestionLabel(): string {
    const amount = this.containerSuggestion?.NumPallets || 0;
    const type = amount > 0 ? 'add' : 'reduce';
    const sign = amount > 0 ? '+' : '';
    return this.$t(`container-calculation.suggestion.${type}`, {
      amount: `${sign}${amount}`,
    }) as string;
  }

  toggleActive(active: boolean) {
    this.$store.commit(`${this.cartType}/toggleActiveGroup`, {
      id: this.groupLabel,
      active,
      user: this.$store.state.auth.user.username,
    });
  }

  setQuantity(product: SapProduct, quantity: number, unit: string) {
    this.$store.commit(`${this.cartType}/setCartItemQuantity`, {
      product,
      unit,
      quantity,
    });
    this.$emit('change');
  }

  hasContainerSuggestion(product: SapProduct): boolean {
    return (
      !!this.containerSuggestion &&
      this.containerSuggestion.ProductNumber === product.ProductNr &&
      !!this.containerSuggestion?.NumPallets
    );
  }

  hasTruckSuggestion(product: SapProduct): boolean {
    if (this.$store.state.order.suggestionCancelled) {
      return false;
    }

    const corrSugg = this.$store.state.order.suggestion?.CorrectionSuggestion;
    const suggestion = corrSugg?.ProductNr;

    if (!suggestion) {
      return false;
    }
    return product.ProductNr === suggestion;
  }

  setContainerSuggestion(product: SapProduct) {
    const delta = this.containerSuggestion?.NumPallets || 0;
    this.setSuggestion(product, delta);
  }

  setTruckSuggestion(suggestion: OrderSuggestionCorrectionSuggestion) {
    const delta = suggestion.NumPallets || 0;

    const product = this.$store.getters['order/productByNr'](
      suggestion.ProductNr,
    );
    this.$store.commit('order/setSuggestionAccepted', true);
    this.setSuggestion(product, delta);
    // Ignore the cart change if it is caused by accepting the suggestion
    this.$store.commit('order/setCartChanged', false);
  }

  setSuggestion(product: SapProduct, delta: number) {
    if (delta !== 0) {
      const newQuantity = this.getQuantityNumber(product, 'PAL') + delta;
      this.setQuantity(product, newQuantity, 'PAL');
    }
  }

  getQuantityNumber(product: SapProduct, unit: string): number {
    const getCartItem = this.$store.getters[`${this.cartType}/getCartItem`];
    const item = getCartItem(product, unit);
    if (!item) {
      return 0;
    }
    return parseInt(item.Quantity, 10);
  }

  getQuantity(product: SapProduct, unit: string): string {
    const quantity = this.getQuantityNumber(product, unit);
    return quantity ? `${quantity}` : '';
  }

  isDisabled(product: SapProductForCustomer): boolean {
    if (this.type !== 'order') {
      return false;
    }

    const limitWarehouse =
      this.$store.getters['order/activePreferredWarehouse'];
    if (limitWarehouse === '') {
      return false;
    }

    return (
      product.DeliveryPlantCode != null &&
      product.DeliveryPlantCode !== limitWarehouse
    );
  }

  calculateBoxes(product: SapProduct): number {
    const { BoxesPerLayer, BoxesPerPallet } = product;
    let boxes = 0;
    if (BoxesPerLayer !== undefined && BoxesPerLayer !== null) {
      const perLayer = parseInt(BoxesPerLayer, 10);
      const layers = this.getQuantityNumber(product, 'LAY');
      boxes += perLayer * layers;
    }
    if (BoxesPerPallet !== undefined && BoxesPerPallet !== null) {
      const perPallet = parseInt(BoxesPerPallet, 10);
      const pallets = this.getQuantityNumber(product, 'PAL');
      boxes += perPallet * pallets;
    }
    return boxes;
  }

  calculateLayers(product: SapProduct): number {
    const { LayersPerPallet } = product;
    let layers = 0;
    if (LayersPerPallet !== undefined && LayersPerPallet !== null) {
      const perPallet = parseInt(LayersPerPallet, 10);
      const pallets = this.getQuantityNumber(product, 'PAL');
      layers += perPallet * pallets;
    }
    return layers;
  }

  agreementItem(product: SapProduct): SalesQuoteItem {
    return this.agreement?.items?.find(
      (item) => item.sap_product === product,
    ) as SalesQuoteItem;
  }

  calculateClaim(product: SapProduct): ClaimTotals {
    return this.$store.getters['claim/getItemCalculation'](product);
  }
}
